/**
 * Naming coverage card at the top of `/me/payees` (TMMAPP-0032). Values come from
 * `GET /me/payees/naming-status`; the bar is a ratio of two served counts only.
 * "Name them with AI" always opens the pre-flight — never calls `run` itself. A refusal the person
 * can't fix here (served, or from a run) replaces the button with its sentence; a fixable wallet
 * refusal keeps the button, and the pre-flight shows `AiWalletPrompt` (TMMAPP-0136 item 4c).
 *
 * TMMAPP-0162: with no payee named automatically, the free line and its empty bar aren't drawn
 * (item 8). The skeleton is the first load's only: a reload keeps the card and its last numbers
 * until the new ones arrive, and a failed reload keeps them (item 10).
 *
 * TMMAPP-0163: the estimate is the whole job again (`estimatedCallCount`, item 1). The pre-flight's
 * Continue starts a background run, and the card follows it in place (`useNamingRun`, item 3):
 * "Naming {total} payees… {evaluated} looked at, {proposed} suggested", a determinate bar and Stop,
 * in the slot the offer had, so nothing below moves. As `proposed` grows the host re-reads the names
 * to review (`onRan`); when the run ends the card says how, re-reads its numbers in place, and a
 * gate's stop shows that reason's copy and action. A served `bySource` replaces the free line with
 * the split (`NamingSplit`, item 5). The naming mode (item 6): under FREE_FIRST a "Use AI for all
 * naming" link, under AI_ALL "AI checks every name" with Change; both open the same choice in a small
 * dialog, and a switch that suggests a run offers **Name them with AI now**, which opens the
 * pre-flight (never a run by itself). Arriving from Settings' offer (`NAME_WITH_AI_STATE`) opens the
 * pre-flight once.
 *
 * TMMAPP-0164:
 * - While an ended run with a gate reason is on screen, `RunStatus` owns the reason and its action:
 *   the card's own reason alert and the offer stay hidden until the person starts again (item 1).
 * - The progress line is quiet to screen readers while the run runs (`aria-live="off"`; the bar keeps
 *   `aria-valuenow`); only the end message is announced, in a polite live region (item 3).
 * - With nothing left in AI's scope (`opaqueRemaining` 0) and texts still unnamed, the card says
 *   "{n} left for you to name. AI had no suggestion for these." in the offer's place, with **Name
 *   them** (`onShowUnnamed`), which shows the "Left for you to name" section. It isn't open work
 *   (item 11).
 */
import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { aiDisabledCopy, errorCopy } from '@/api/errorCopy';
import { AI_WALLET_REASONS, type AiWalletReason } from '@/api/errors';
import type { PayeeNamingMode } from '@/api/me';
import {
  payeeSuggestionsApi,
  type PayeeNamingRun,
  type PayeeNamingStatus,
} from '@/api/payeeSuggestions';
import { ErrorState } from '@/components/ErrorState';
import { LiveRegion } from '@/components/LiveRegion';
import { ProgressTrack } from '@/components/ProgressTrack';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { settingsHref } from '@/features/settings/settingsHref';
import { useMe } from '@/session/meHooks';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import type { NotifyOptions } from '@/shell/NotifyProvider';
import { AiNamingPreflight } from './AiNamingPreflight';
import { NamingModeDialog } from './NamingModeChoice';
import { switchedCopyKey, wantsNameWithAi } from './namingMode';
import { NamingSplit } from './NamingSplit';
import { splitParts } from './namingSplitParts';
import { useNamingRun } from './useNamingRun';

export interface NamingStatusCardProps {
  /** Bump to force a refetch (e.g. after a proposal decision). */
  reloadToken?: number | undefined;
  /** A notice; a mode switch that suggests a run carries "Name them with AI now". */
  onNotify?: ((message: string, options?: NotifyOptions) => void) | undefined;
  /**
   * Fired as a run's suggestions grow (at most once per status read) and when it ends, so the
   * proposal list re-reads in place.
   */
  onRan?: (() => void) | undefined;
  /**
   * Whether the card still offers naming work ("Name them with AI"), reported once its read settles
   * (`false` on a failed read). The page counts it as an item to review (TMMAPP-0160 item 3).
   */
  onOpenWork?: ((open: boolean) => void) | undefined;
  /**
   * "Name them" beside the left-for-you sentence (TMMAPP-0164 items 11–12): the host shows the "Left
   * for you to name" section and moves focus to its heading. Without it the sentence has no link.
   */
  onShowUnnamed?: (() => void) | undefined;
  /** The served texts kept unnamed (`bySource.KEPT_UNNAMED`), reported on every settled read. */
  onKeptCount?: ((kept: number) => void) | undefined;
}

/** The last numbers stay on screen while a reload is in flight (`reloading`). */
type State =
  | { status: 'loading' }
  | { status: 'ready'; data: PayeeNamingStatus; reloading: boolean }
  | { status: 'error'; error: unknown };

function coveragePercent(data: PayeeNamingStatus): number {
  const named = data.namedDeterministically;
  const total = data.distinctUnresolved;
  if (total <= 0) return named > 0 ? 100 : 0;
  return Math.min(100, Math.round((named / total) * 100));
}

const REASON_KEYS = new Set([
  'GLOBAL',
  'FEATURE',
  'USER',
  'DAILY_CAP',
  // TMMAPI-0151: the wallet's reasons; BILLING and MONTHLY_CAP are retired.
  'PERSON_CAP',
  'WALLET_EMPTY',
  'NO_PRICE',
  'FORBIDDEN',
]);

/**
 * A refusal the person can't fix here (TMMAPP-0136 item 4c): the card says why in place of the
 * button, and the pre-flight never opens. Each is its reason's sentence; a bare `AI_DISABLED`
 * (no served reason) reads through `aiDisabledCopy`, the one reader of the generic "AI is off".
 */
const UNFIXABLE = {
  NO_PRICE: 'aiWallet.prompt.noPrice',
  SUSPENDED: 'ai.reason.SUSPENDED',
  SUBSCRIPTION_INACTIVE: 'ai.reason.SUBSCRIPTION_INACTIVE',
  AI_DISABLED: null,
} as const;
type UnfixableReason = keyof typeof UNFIXABLE;

function unfixable(reason: string | null): reason is UnfixableReason {
  return reason !== null && Object.hasOwn(UNFIXABLE, reason);
}

function unfixableText(t: ReturnType<typeof useTranslation>['t'], reason: UnfixableReason) {
  return reason === 'AI_DISABLED'
    ? aiDisabledCopy(t, {
        reason: null,
        availableMinor: null,
        firstPurchaseRequired: false,
        debtMinor: null,
      })
    : t(UNFIXABLE[reason]);
}

/** A wallet refusal the person can fix: the button stays, and the pre-flight shows `AiWalletPrompt`. */
const FIXABLE_WALLET = new Set(['WALLET_EMPTY', 'AI_DEBT', 'PERSON_CAP']);

function walletReason(reason: string | null): AiWalletReason | null {
  return (AI_WALLET_REASONS as readonly (string | null)[]).includes(reason)
    ? (reason as AiWalletReason)
    : null;
}

function stopReasonOf(
  data: PayeeNamingStatus,
  refused: UnfixableReason | null,
): UnfixableReason | null {
  return refused ?? (unfixable(data.aiUnavailableReason) ? data.aiUnavailableReason : null);
}

/** The card offers "Name them with AI": nothing stops it, and AI is on or a fixable wallet refusal. */
function offersAi(data: PayeeNamingStatus, refused: UnfixableReason | null): boolean {
  return (
    stopReasonOf(data, refused) === null &&
    (data.aiAvailable ||
      (data.aiUnavailableReason !== null && FIXABLE_WALLET.has(data.aiUnavailableReason)))
  );
}

/**
 * An ended run that stopped at the AI gate (item 1): `RunStatus` says the reason and its action (the
 * wallet prompt, the unfixable sentence, or the gate's reason), so the card says neither again.
 */
function gateStopped(run: PayeeNamingRun | null): boolean {
  if (run === null || run.state !== 'STOPPED' || run.stopReason === null) return false;
  const reason = run.stopReason;
  return walletReason(reason) !== null || unfixable(reason) || REASON_KEYS.has(reason);
}

/** The run's share done, 0–100: a finished run is whole. */
function runPercent(run: PayeeNamingRun): number {
  if (run.state === 'DONE') return 100;
  if (run.total <= 0) return 0;
  return Math.min(100, Math.round((run.evaluated / run.total) * 100));
}

export function NamingStatusCard({
  reloadToken = 0,
  onNotify,
  onRan,
  onOpenWork,
  onShowUnnamed,
  onKeptCount,
}: NamingStatusCardProps) {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [preflightOpen, setPreflightOpen] = useState(false);
  // A run this card started was refused for a reason the person can't fix here.
  const [refused, setRefused] = useState<UnfixableReason | null>(null);
  const [modeDialog, setModeDialog] = useState<PayeeNamingMode | null>(null);
  const [stopping, setStopping] = useState(false);

  const load = useCallback(async () => {
    setState((prev) =>
      prev.status === 'ready' ? { ...prev, reloading: true } : { status: 'loading' },
    );
    try {
      const data = await payeeSuggestionsApi.namingStatus();
      setState({ status: 'ready', data, reloading: false });
    } catch (error) {
      // A failed reload keeps the last numbers; only a first load shows the section error.
      setState((prev) =>
        prev.status === 'ready' ? { ...prev, reloading: false } : { status: 'error', error },
      );
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => {
      void load();
    });
  }, [load, reloadToken]);

  const watch = useNamingRun({
    onProposed: () => onRan?.(),
    onEnded: () => {
      void load();
      onRan?.();
    },
  });
  const run = watch.run;
  const running = run !== null && run.state === 'RUNNING';
  // Item 1: an ended gate stop on screen holds the card's own reason and offer.
  const held = running || gateStopped(run);

  // A load or reload in flight reports nothing, so the page keeps its last count. A run in flight
  // is already the naming work: the card offers none while it runs.
  const openWork =
    state.status === 'ready' && !state.reloading
      ? !held && state.data.opaqueRemaining > 0 && offersAi(state.data, refused)
      : state.status === 'error'
        ? false
        : null;
  useEffect(() => {
    if (openWork !== null) onOpenWork?.(openWork);
  }, [openWork, onOpenWork]);
  const kept =
    state.status === 'ready' && !state.reloading ? (state.data.bySource?.KEPT_UNNAMED ?? 0) : null;
  useEffect(() => {
    if (kept !== null) onKeptCount?.(kept);
  }, [kept, onKeptCount]);

  const openPreflight = useCallback(() => {
    watch.clear();
    setPreflightOpen(true);
  }, [watch]);

  // Settings' "Name them with AI now" lands here: the dialog opens once, when it can.
  const arrivedToName = wantsNameWithAi(location.state);
  const ready = state.status === 'ready' && !state.reloading;
  const canOffer =
    ready && state.data.opaqueRemaining > 0 && offersAi(state.data, refused) && !held;
  const handledArrival = useRef(false);
  useEffect(() => {
    if (!arrivedToName || handledArrival.current || !ready) return;
    handledArrival.current = true;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    if (canOffer) queueMicrotask(openPreflight);
  }, [arrivedToName, ready, canOffer, navigate, location, openPreflight]);

  const modeLink = useSubscriberAction((event: MouseEvent<HTMLButtonElement>) => {
    const wanted = event.currentTarget.dataset.modeLink;
    setModeDialog(wanted === 'AI_ALL' ? 'AI_ALL' : me.user.payeeNamingMode);
  });

  async function stop() {
    if (stopping) return;
    setStopping(true);
    try {
      await watch.cancel();
    } catch (err) {
      onNotify?.(errorCopy(t, err, 'accounts.errorGeneric'), { severity: 'error' });
    } finally {
      setStopping(false);
    }
  }

  if (state.status === 'loading') {
    return <Skeleton variant="rounded" height={96} role="status" aria-label={t('naming.title')} />;
  }

  if (state.status === 'error') {
    return (
      <ErrorState
        variant="section"
        error={state.error}
        what={t('errors.what.namingStatus')}
        onRetry={() => void load()}
      />
    );
  }

  const { data } = state;
  const percent = coveragePercent(data);
  const userOff = data.aiUnavailableReason === 'USER';
  const reasonText = (reason: string | null) =>
    reason !== null && REASON_KEYS.has(reason)
      ? t(`naming.reason.${reason}` as 'naming.reason.fallback')
      : t('naming.reason.fallback');
  const stopReason = stopReasonOf(data, refused);
  const offerAi = offersAi(data, refused);
  const split = data.bySource !== null && splitParts(data.bySource).length > 0;
  // Item 11: texts AI evaluated and couldn't name are out of its scope; the person names them.
  // The server counts them (`unnamedNoSuggestion`); where it isn't served it reads 0, so the
  // sentence is hidden there, never estimated (TMMAPP-0170).
  const leftForYou = data.opaqueRemaining !== 0 ? 0 : data.unnamedNoSuggestion;
  const mode = me.user.payeeNamingMode;
  const automationLink = (
    <Link component={RouterLink} to={settingsHref('ai-automation')}>
      {t('naming.openAutomation')}
    </Link>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }} role="group" aria-label={t('naming.title')}>
      <Stack spacing={1.5}>
        <Typography variant="h2">{t('naming.title')}</Typography>
        {/* Item 5: the split by source replaces the free line when it is served. */}
        {split && data.bySource !== null && <NamingSplit bySource={data.bySource} />}
        {/* A zero isn't said: the card leads with the unnamed count and its offer (item 8). */}
        {!split && data.namedDeterministically > 0 && (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }} data-naming-free="">
              {t('naming.freeLine', { named: data.namedDeterministically })}
            </Typography>
            <Box>
              {/* The shared filled track (TMMAPP-0156 item 4). */}
              <ProgressTrack value={percent} height={8} label={t('naming.title')} />
            </Box>
          </>
        )}

        {run !== null && (
          <RunStatus
            run={run}
            stopping={stopping}
            onStop={() => void stop()}
            onTryAgain={openPreflight}
            reasonText={reasonText}
            automationLink={automationLink}
          />
        )}

        {!held && data.opaqueRemaining > 0 && offerAi && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ gap: 1.5, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
          >
            <Typography data-naming-offer="">
              {t('naming.aiOffer', {
                count: data.opaqueRemaining,
                requests: t('naming.aiRequests', { count: data.estimatedCallCount }),
              })}
            </Typography>
            <Button variant="contained" onClick={openPreflight}>
              {t('naming.nameWithAi')}
            </Button>
          </Stack>
        )}

        {!running && leftForYou > 0 && (
          <Typography data-naming-left-for-you="">
            {t('naming.leftForYou', { count: leftForYou })}
            {onShowUnnamed !== undefined && (
              <>
                {' '}
                <Link
                  component="button"
                  type="button"
                  variant="body1"
                  onClick={onShowUnnamed}
                  sx={{ verticalAlign: 'baseline' }}
                >
                  {t('naming.nameThem')}
                </Link>
              </>
            )}
          </Typography>
        )}

        {!held && data.opaqueRemaining > 0 && stopReason !== null && (
          <Alert severity="info" data-ai-stopped={stopReason}>
            {unfixableText(t, stopReason)}
          </Alert>
        )}

        {!held && data.opaqueRemaining > 0 && stopReason === null && !offerAi && (
          <Alert severity="info">
            {t('naming.aiUnavailable', { reason: reasonText(data.aiUnavailableReason) })}
            {userOff && <> {automationLink}</>}
          </Alert>
        )}

        {/* Item 6: the mode, easy to find. */}
        <Typography variant="body2" data-naming-mode-line={mode}>
          {mode === 'AI_ALL' && <>{t('naming.mode.aiAllOn')} </>}
          <Link
            component="button"
            type="button"
            variant="body2"
            data-mode-link={mode === 'AI_ALL' ? 'current' : 'AI_ALL'}
            aria-describedby={modeLink.descriptionId}
            onClick={modeLink.run}
            sx={{ verticalAlign: 'baseline' }}
          >
            {mode === 'AI_ALL' ? t('naming.mode.change') : t('naming.mode.aiAll')}
          </Link>
          <SubscriberActionHint id={modeLink.descriptionId} description={modeLink.description} />
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('naming.skipNote')}
        </Typography>
      </Stack>

      <AiNamingPreflight
        open={preflightOpen}
        estimatedCallCount={data.estimatedCallCount}
        aiAvailable={data.aiAvailable}
        aiUnavailableReason={data.aiUnavailableReason}
        onClose={() => setPreflightOpen(false)}
        onStarted={(runId) => {
          setPreflightOpen(false);
          watch.follow(runId, data.opaqueRemaining);
        }}
        onModeChanged={() => void load()}
        onRefused={(reason) => {
          if (!unfixable(reason)) return false;
          setPreflightOpen(false);
          setRefused(reason);
          return true;
        }}
      />
      <NamingModeDialog
        open={modeDialog !== null}
        initial={modeDialog ?? mode}
        onClose={() => setModeDialog(null)}
        onSaved={(next, runSuggested) => {
          setModeDialog(null);
          void load();
          onNotify?.(
            t(switchedCopyKey(next)),
            runSuggested && !running
              ? { action: { label: t('naming.mode.nameNow'), onClick: openPreflight } }
              : undefined,
          );
        }}
      />
    </Paper>
  );
}

/**
 * The run in the offer's slot: while it runs, its progress and Stop; once it ends, how it ended, with
 * the same bar (a finished run whole), so the card keeps its height.
 */
function RunStatus({
  run,
  stopping,
  onStop,
  onTryAgain,
  reasonText,
  automationLink,
}: {
  run: PayeeNamingRun;
  stopping: boolean;
  onStop: () => void;
  onTryAgain: () => void;
  reasonText: (reason: string | null) => string;
  automationLink: ReactNode;
}) {
  const { t } = useTranslation();
  const reason = run.state === 'STOPPED' ? run.stopReason : null;
  const ended = run.state !== 'RUNNING';
  const wallet = walletReason(reason);
  const soFar = t('naming.run.soFar', { count: run.proposed });
  let message: string;
  let action: ReactNode = null;
  if (run.state === 'RUNNING') {
    message = t('naming.run.progress', {
      count: run.total,
      evaluated: run.evaluated,
      proposed: run.proposed,
    });
  } else if (run.state === 'DONE') {
    message = t('naming.run.done', { count: run.proposed });
  } else if (reason === 'CANCELLED') {
    message = t('naming.run.cancelled', { count: run.proposed });
  } else if (reason === 'INTERRUPTED') {
    message = t('naming.run.interrupted');
  } else if (wallet !== null) {
    // The gate's wallet reasons: what the pre-flight would say, with its top-up (item 3).
    message = soFar;
    action = (
      <AiWalletPrompt
        refusal={{
          reason: wallet,
          availableMinor: null,
          firstPurchaseRequired: false,
          debtMinor: null,
        }}
        onTryAgain={onTryAgain}
      />
    );
  } else if (reason !== null && unfixable(reason)) {
    message = soFar;
    action = <Alert severity="info">{unfixableText(t, reason)}</Alert>;
  } else if (reason !== null && REASON_KEYS.has(reason)) {
    message = soFar;
    action = (
      <Alert severity="info">
        {t('naming.aiUnavailable', { reason: reasonText(reason) })}
        {reason === 'USER' && <> {automationLink}</>}
      </Alert>
    );
  } else {
    // PROVIDER_FAILED, ERROR, or a reason this build doesn't know: neutral words (item 7).
    message = t('naming.run.stopped', { count: run.proposed });
  }
  return (
    <Stack spacing={1} data-naming-run={run.state}>
      <Stack
        direction="row"
        // The Stop button's height, so the row doesn't shrink when the run ends.
        sx={{ gap: 1.5, alignItems: 'center', justifyContent: 'space-between', minHeight: 36 }}
      >
        {/* Item 3: the progress line doesn't chatter; the end is said once, below. */}
        <Typography aria-live="off" data-naming-run-message="">
          {message}
        </Typography>
        {run.state === 'RUNNING' && (
          <Button variant="outlined" size="small" disabled={stopping} onClick={onStop}>
            {t('naming.run.stop')}
          </Button>
        )}
      </Stack>
      <LinearProgress
        variant="determinate"
        value={runPercent(run)}
        aria-label={t('naming.run.progressLabel')}
      />
      {action}
      <Box component="span" data-naming-run-announce="">
        <LiveRegion text={ended ? message : ''} />
      </Box>
    </Stack>
  );
}
